import { observer } from "mobx-react-lite";
import { useStores } from "stores";
import { CustomButton } from "ui";
import { ExecuteBidModal } from "./ExecuteBidModal";
import { BID_WALLET_OPERATION_ENUM } from "interfaces";
import { useEffect, useState, useRef } from "react";
import { useAmlCheckStatusEvents, fetchId } from "utils";
import {
  CHECK_WALLETS_REQUIRED,
  INITIATE_WALLET_CHECK_MESSAGE,
  WALLET_CHECKS_CARRIED_MESSAGE,
  NEED_APPROVE_RISK_MANAGER_MESSAGE,
  INTERVAL_CHECKING_WALLET,
  WE_ENV_WS_FALLBACK_TIMEOUT_IN_SECONDS,
} from "constants/network";
import { Flex } from "antd";

interface ExecuteBidBlockProps {
  validWalletsChecks: {
    isValid: boolean;
    hasApprovalNeeded: boolean;
    lastCheckTime: Date | null;
  };
  onWalletChecksUpdated: () => Promise<void>;
}

export const ExecuteBidBlock = observer(({ validWalletsChecks, onWalletChecksUpdated }: ExecuteBidBlockProps) => {
  const { actionModals, bidCard } = useStores();
  const { subscribeToAmlCheckStatus } = useAmlCheckStatusEvents();

  useEffect(() => {
    if (bidCard.bid?.id) {
      const unsubscribe = subscribeToAmlCheckStatus(bidCard.bid?.id, (eventData) => {
        clearFallbackStartTimeout();
        clearPollingInterval();
        setLoading(false);
      });

      return unsubscribe;
    }
  }, [subscribeToAmlCheckStatus, bidCard.bid?.id]);

  const { bid, amlCheckStatusData: amlCheckStatus } = bidCard;

  const [loading, setLoading] = useState(false);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fallbackStartTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPollingInterval = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const clearFallbackStartTimeout = () => {
    if (fallbackStartTimeoutRef.current) {
      clearTimeout(fallbackStartTimeoutRef.current);
      fallbackStartTimeoutRef.current = null;
    }
  };

  const currentAmlCheckStatus = amlCheckStatus?.find(
    (item) => item.requestAction === BID_WALLET_OPERATION_ENUM.EXECUTE
  );

  useEffect(() => {
    if (currentAmlCheckStatus?.checksCompleted) {
      clearFallbackStartTimeout();
      clearPollingInterval();
      setLoading(false);
    }
  }, [currentAmlCheckStatus]);

  useEffect(() => {
    return () => {
      clearFallbackStartTimeout();
      clearPollingInterval();
    };
  }, []);

  const checkWalletsHandler = async () => {
    setLoading(true);
    clearFallbackStartTimeout();
    clearPollingInterval();

    if (bid?.transitWallet && bid?.destinationWallet) {
      await fetchId({ walletAddress: bid?.transitWallet, requestId: bid?.id });
      await fetchId({ walletAddress: bid?.destinationWallet, requestId: bid?.id });

      fallbackStartTimeoutRef.current = setTimeout(
        () => {
          pollingIntervalRef.current = setInterval(() => {
            bidCard.fetchAmlCheckStatus(bid.id).catch((e) => console.error(e));
          }, Number(INTERVAL_CHECKING_WALLET));
        },
        Number(WE_ENV_WS_FALLBACK_TIMEOUT_IN_SECONDS) * 1000
      );
    } else {
      setLoading(false);
    }
  };

  const isExecuteButtonDisabled = () => {
    if (!currentAmlCheckStatus) return true;

    return !(currentAmlCheckStatus.checksCompleted && currentAmlCheckStatus.checksSuccess);
  };

  const isCheckButtonDisabled = () => {
    if (!currentAmlCheckStatus) return false;
    return currentAmlCheckStatus.checksCompleted;
  };

  const getTooltipForExecuteButton = () => {
    if (loading) return undefined;
    if (!currentAmlCheckStatus) return undefined;

    if (currentAmlCheckStatus.checksCompleted && !currentAmlCheckStatus.checksSuccess) {
      return NEED_APPROVE_RISK_MANAGER_MESSAGE;
    }

    if (!currentAmlCheckStatus.checksCompleted) {
      return CHECK_WALLETS_REQUIRED;
    }

    return `Заявка будет исполнена несколькими транзакциями. Количество транзакций ${bid?.splitCount ?? 0}`;
  };

  const getTooltipForCheckButton = () => {
    if (currentAmlCheckStatus && currentAmlCheckStatus.checksCompleted) {
      return WALLET_CHECKS_CARRIED_MESSAGE;
    } else {
      return INITIATE_WALLET_CHECK_MESSAGE;
    }
  };

  const executeBidHandler = () => {
    actionModals.open(() => <ExecuteBidModal />, {
      width: "600px",
      noValidation: true,
      title: "Исполнение заявки",
      onClose: () => actionModals.closeAll(),
    });
  };

  return (
    <Flex gap="small" align="start">
      <CustomButton
        type="primary"
        onClick={checkWalletsHandler}
        loading={loading}
        disabled={isCheckButtonDisabled()}
        tooltip={getTooltipForCheckButton()}
      >
        Проверить кошельки
      </CustomButton>
      <CustomButton
        type="primary"
        onClick={executeBidHandler}
        loading={loading}
        disabled={isExecuteButtonDisabled()}
        tooltip={getTooltipForExecuteButton()}
      >
        Исполнить заявку
      </CustomButton>
    </Flex>
  );
});
